import { z } from "zod";

import { EmbedSettingsSchema } from "../../../../schemas/Deployment";

/**
 * Embed Schemas for Widget Operations
 *
 * These schemas support the embed widget system endpoints:
 * - Knowledge file upload/retrieval
 * - Chat attachment uploads
 * - Widget configuration
 * - Embed metadata and statistics
 *
 * PUBLIC endpoints use @AllowUnauthenticated() decorator.
 * Auth model MUST match exactly when migrating.
 *
 * See: /apps/backend/src/integrations/integrations/embed/embed.controller.ts
 */

// ============================================================================
// COMMON PATH VARIABLES
// ============================================================================

export const EmbedIdPathVarsSchema = z.object({
  id: z.string().uuid()
});

// ============================================================================
// KNOWLEDGE FILE SCHEMAS
// ============================================================================

/**
 * Create knowledge file for message - Body
 *
 * POST /api/integrations/embed/knowledge/file
 * POST /api/deployments/embed/knowledge/file (new canonical)
 *
 * Note: File is uploaded via multipart/form-data, not in body
 */
export const CreateKnowledgeFileBodySchema = z.object({
  /** Deployment ID */
  deploymentId: z.string().uuid(),
  /** Session ID for session tracking */
  sessionId: z.string().min(1)
});

/**
 * Create knowledge file - Response
 */
export const CreateKnowledgeFileResponseSchema = z.object({
  /** Created file ID */
  id: z.string(),
  /** File URL */
  url: z.string().url().optional(),
  /** File name */
  name: z.string().optional(),
  /** File size in bytes */
  size: z.number().optional(),
  /** File MIME type */
  mimeType: z.string().optional()
});

/**
 * Get knowledge file - Path Variables
 *
 * GET /api/integrations/embed/knowledge/file/:id
 * GET /api/deployments/embed/knowledge/file/:id (new canonical)
 */
export const GetKnowledgeFilePathVarsSchema = z.object({
  id: z.string().min(1)
});

/**
 * Get knowledge file - Query Parameters
 *
 * The visitor's session id is NOT here: it travels in the `x-session-id` header,
 * as on every other widget route, so it stays out of URLs, access logs and
 * `Referer`. A `sessionId` still sent in the query is stripped and ignored.
 */
export const GetKnowledgeFileQuerySchema = z.object({
  deploymentId: z.string().uuid()
});

/**
 * Get knowledge file - Response
 *
 * A link to a file the visitor's own conversation carries — never its extracted
 * text. The route is anonymous, so the body of a document is not a field here.
 */
export const GetKnowledgeFileResponseSchema = z.object({
  /** File ID */
  id: z.string(),
  /** Presigned file URL, or "" when the row holds no link */
  url: z.string(),
  /** File name */
  name: z.string(),
  /** File size in bytes */
  size: z.number().optional()
});

// ============================================================================
// CHAT ATTACHMENTS SCHEMAS
// ============================================================================

/**
 * Upload chat attachments - Query Parameters
 *
 * POST /api/integrations/embed/uploadChatAttachments
 * POST /api/deployments/embed/uploadChatAttachments (new canonical)
 *
 * PUBLIC endpoint (@AllowUnauthenticated)
 * Note: Files uploaded via multipart/form-data
 */
export const UploadChatAttachmentsQuerySchema = z.object({
  /** Deployment ID */
  deploymentId: z.string().uuid(),
  /**
   * The widget's session id.
   *
   * Required on the wire and read by nothing on the server. It is not a
   * credential: this route is `@AllowUnauthenticated`, and the only value it
   * decides on is `deploymentId`.
   */
  sessionId: z.string().min(1),
  /** Generate thumbnails for images (default: true) */
  generateThumbnails: z
    .string()
    .transform((v) => v === "true")
    .optional(),
  /** Maximum thumbnail dimension in pixels (default: 200) */
  maxThumbnailSize: z
    .string()
    .transform((v) => parseInt(v, 10))
    .optional()
});

/**
 * Individual attachment in upload response
 */
/**
 * Uploaded attachment - Discriminated union based on status
 */
export const UploadedAttachmentSchema = z.discriminatedUnion("status", [
  // Success case - matches backend UploadedAttachment type
  z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: z.string(),
    size: z.number(),
    url: z.string().url(),
    thumbnailUrl: z.string().url().optional(),
    isImage: z.boolean(),
    status: z.literal("DONE"),
    metadata: z.object({
      width: z.number().optional(),
      height: z.number().optional(),
      mimeType: z.string(),
      thumbnailUrl: z.string().optional()
    })
  }),
  // Error case - only has id, name, status, error object
  z.object({
    id: z.string().uuid(),
    name: z.string(),
    status: z.literal("ERROR"),
    error: z.object({
      message: z.string(),
      code: z.string()
    })
  })
]);

/**
 * Upload chat attachments - Response
 */
export const UploadChatAttachmentsResponseSchema = z.object({
  /** Overall success flag */
  success: z.boolean(),
  /** Array of uploaded attachments */
  attachments: z.array(UploadedAttachmentSchema),
  /** Upload summary */
  summary: z.object({
    /** Total files submitted */
    total: z.number(),
    /** Successfully processed */
    succeeded: z.number(),
    /** Failed to process */
    failed: z.number()
  }),
  /** Error message if overall failure */
  error: z.string().optional()
});

// ============================================================================
// WIDGET CONFIG SCHEMAS
// ============================================================================

/**
 * Get widget configuration - Path Variables
 *
 * GET /api/integrations/embed/:id/widget-config
 * GET /api/deployments/embed/:id/widget-config (new canonical)
 *
 * AUTHENTICATED endpoint (requires Clerk JWT)
 */
export const GetWidgetConfigPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Widget configuration response
 *
 * Contains all settings needed for the embed widget appearance.
 * Combines deployment settings with computed/derived values.
 */
export const WidgetConfigResponseSchema = z.object({
  /** Deployment ID */
  id: z.string().uuid(),
  /** Deployment name */
  name: z.string(),
  /** Full embed settings */
  embedSettings: EmbedSettingsSchema,
  /** Widget-specific computed values */
  widget: z.object({
    /** Generated widget URL */
    widgetUrl: z.string().url().optional(),
    /** Public info endpoint URL */
    publicInfoUrl: z.string().url().optional(),
    /** Message endpoint URL */
    messageUrl: z.string().url().optional()
  })
});

// ============================================================================
// EMBED METADATA SCHEMAS
// ============================================================================

/**
 * Get embed metadata - Path Variables
 *
 * GET /api/integrations/embed/:id/metadata
 * GET /api/deployments/embed/:id/metadata (new canonical)
 *
 * AUTHENTICATED endpoint (requires Clerk JWT)
 */
export const GetEmbedMetadataPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Embed metadata response
 */
export const EmbedMetadataResponseSchema = z.object({
  /** Deployment ID */
  id: z.string().uuid(),
  /** Deployment name */
  name: z.string(),
  /** Description */
  description: z.string().nullable(),
  /** Organization ID */
  organizationId: z.string(),
  /** Creation timestamp */
  createdAt: z.string().datetime(),
  /** Last update timestamp */
  updatedAt: z.string().datetime().nullable(),
  /** Deployment status */
  status: z.enum(["ACTIVE", "INACTIVE", "DELETED"]).optional(),
  /** Whether the embed is publicly accessible */
  isPublic: z.boolean().optional()
});

// ============================================================================
// PUBLIC STATUS SCHEMAS
// ============================================================================

/**
 * Check embed public status - Path Variables
 *
 * GET /api/integrations/embed/:id/public-status
 * GET /api/deployments/embed/:id/public-status (new canonical)
 *
 * AUTHENTICATED endpoint (requires Clerk JWT)
 */
export const GetEmbedPublicStatusPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Embed public status response
 */
export const EmbedPublicStatusResponseSchema = z.object({
  /** Whether the embed is publicly accessible */
  isPublic: z.boolean()
});

// ============================================================================
// STATISTICS SCHEMAS
// ============================================================================

/**
 * Get embed statistics - Path Variables
 *
 * GET /api/integrations/embed/:id/statistics
 * GET /api/deployments/embed/:id/statistics (new canonical)
 *
 * AUTHENTICATED endpoint (requires Clerk JWT)
 */
export const GetEmbedStatisticsPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Embed statistics response
 *
 * Basic statistics for embed widget usage.
 * For detailed statistics, use the generic deployment statistics endpoint.
 */
export const EmbedStatisticsResponseSchema = z.object({
  /** Deployment ID */
  deploymentId: z.string().uuid(),
  /** Total number of sessions */
  totalSessions: z.number(),
  /** Total messages sent */
  totalMessages: z.number(),
  /** Total unique users */
  totalUsers: z.number().optional(),
  /** Statistics time range */
  period: z
    .object({
      start: z.string().datetime(),
      end: z.string().datetime()
    })
    .optional()
});

// ============================================================================
// INTERNAL/ADMIN SCHEMAS (CRUD)
// ============================================================================

/**
 * Create embed deployment - Body
 *
 * POST /api/integrations/embed
 * POST /api/deployments/embed (new canonical)
 *
 * AUTHENTICATED endpoint (requires Clerk JWT)
 *
 * Note: Full deployment creation uses the generic CreateDeployment endpoint.
 * This is for embed-specific settings only.
 */
export const CreateEmbedBodySchema = z.object({
  /** Deployment name */
  name: z.string().trim().min(1).max(100),
  /** Description */
  description: z.string().max(500).optional(),
  /** Agent ID - now passed as separate field, stored in Deployment.agentId column */
  agentId: z.string().uuid().optional(),
  /** Embed-specific settings */
  embedSettings: EmbedSettingsSchema.partial().optional(),
  /** Advanced settings */
  advancedSettings: z.record(z.string(), z.any()).optional(),
  /** Assistant settings */
  assistantSettings: z.record(z.string(), z.any()).optional()
});

/**
 * Update embed deployment - Path Variables
 *
 * POST /api/integrations/embed/:id
 * PUT /api/deployments/embed/:id (new canonical)
 *
 * AUTHENTICATED endpoint (requires Clerk JWT)
 */
export const UpdateEmbedPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Update embed deployment - Body
 */
export const UpdateEmbedBodySchema = z.object({
  /** Deployment name */
  name: z.string().trim().min(1).max(100).optional(),
  /** Description */
  description: z.string().max(500).optional().nullable(),
  /** Agent ID - now passed as separate field, stored in Deployment.agentId column */
  agentId: z.string().uuid().optional(),
  /** Embed-specific settings (partial update) */
  embedSettings: EmbedSettingsSchema.partial().optional(),
  /** Advanced settings (full replacement) */
  advancedSettings: z.record(z.string(), z.any()).optional(),
  /** Assistant settings (full replacement) */
  assistantSettings: z.record(z.string(), z.any()).optional()
});

/**
 * Get embed by ID - Path Variables
 *
 * GET /api/integrations/embed/:id
 * GET /api/deployments/embed/:id (new canonical)
 *
 * AUTHENTICATED endpoint (requires Clerk JWT)
 */
export const GetEmbedByIdPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Delete embed - Path Variables
 *
 * POST /api/integrations/embed/:id/delete
 * DELETE /api/deployments/embed/:id (new canonical)
 *
 * AUTHENTICATED endpoint (requires Clerk JWT)
 */
export const DeleteEmbedPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Delete embed - Response
 */
export const DeleteEmbedResponseSchema = z.object({
  /** Success flag */
  success: z.boolean(),
  /** Deleted deployment ID */
  deletedId: z.string().uuid().optional()
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

// Path variables
export type EmbedIdPathVars = z.infer<typeof EmbedIdPathVarsSchema>;
export type GetKnowledgeFilePathVars = z.infer<typeof GetKnowledgeFilePathVarsSchema>;
export type GetWidgetConfigPathVars = z.infer<typeof GetWidgetConfigPathVarsSchema>;
export type GetEmbedMetadataPathVars = z.infer<typeof GetEmbedMetadataPathVarsSchema>;
export type GetEmbedPublicStatusPathVars = z.infer<typeof GetEmbedPublicStatusPathVarsSchema>;
export type GetEmbedStatisticsPathVars = z.infer<typeof GetEmbedStatisticsPathVarsSchema>;
export type UpdateEmbedPathVars = z.infer<typeof UpdateEmbedPathVarsSchema>;
export type GetEmbedByIdPathVars = z.infer<typeof GetEmbedByIdPathVarsSchema>;
export type DeleteEmbedPathVars = z.infer<typeof DeleteEmbedPathVarsSchema>;

// Query parameters
export type GetKnowledgeFileQuery = z.infer<typeof GetKnowledgeFileQuerySchema>;
export type UploadChatAttachmentsQuery = z.infer<typeof UploadChatAttachmentsQuerySchema>;

// Request bodies
export type CreateKnowledgeFileBody = z.infer<typeof CreateKnowledgeFileBodySchema>;
export type CreateEmbedBody = z.infer<typeof CreateEmbedBodySchema>;
export type UpdateEmbedBody = z.infer<typeof UpdateEmbedBodySchema>;

// Responses
export type CreateKnowledgeFileResponse = z.infer<typeof CreateKnowledgeFileResponseSchema>;
export type GetKnowledgeFileResponse = z.infer<typeof GetKnowledgeFileResponseSchema>;
export type UploadedAttachment = z.infer<typeof UploadedAttachmentSchema>;
export type UploadChatAttachmentsResponse = z.infer<typeof UploadChatAttachmentsResponseSchema>;
export type WidgetConfigResponse = z.infer<typeof WidgetConfigResponseSchema>;
export type EmbedMetadataResponse = z.infer<typeof EmbedMetadataResponseSchema>;
export type EmbedPublicStatusResponse = z.infer<typeof EmbedPublicStatusResponseSchema>;
export type EmbedStatisticsResponse = z.infer<typeof EmbedStatisticsResponseSchema>;
export type DeleteEmbedResponse = z.infer<typeof DeleteEmbedResponseSchema>;

// Re-export agent shape used by DeploymentResponse
export type { EmbedAgent } from "./embed-public.schema";
